import styled from '@emotion/styled';
import { Spacings, Fonts } from '@styles/index';
import Button, { ButtonVariantType } from 'aquilla/core/molecules/Button';
import Typography from 'aquilla/core/atoms/Typography';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useRouter } from 'next/router';
import { CHECKOUT_STARTED } from '@constants/segment';
import {
  browseTopSpacing,
  dayTitleLeftSpacing,
  plannerItemTypeHeightMap
} from '../planner-constants';

interface OuterWrapperProps {
  $height: number;
}

interface WrapperProps {
  $backgroundColor?: string;
  $imageURL?: string;
  $paddingTop: string;
}

interface StyledButtonProps {
  $bottom: string;
}

interface CardCssProperty {
  background_color?: string;
  text_color?: string;
  description_color?: string;
  button_variant: string;
}

interface ItemInfoInner {
  image_url?: string;
  cta_text?: string;
  card_css_property?: CardCssProperty;
}

interface ItemInfo {
  item_info?: ItemInfoInner;
  description?: string;
  title?: string;
  header_text?: string;
}

interface MetaItem {
  plannerItemType: string;
  itemInfo?: ItemInfo;
}

interface PaidTrialCardProps {
  metaItem: MetaItem;
}

const OuterWrapper = styled.div<OuterWrapperProps>`
  padding-top: ${browseTopSpacing}px;
  height: ${({ $height }) => $height}px;
`;

const Wrapper = styled.div<WrapperProps>`
  margin-left: ${dayTitleLeftSpacing};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  background-image: ${({ $imageURL }) => $imageURL && `url(${$imageURL})`};
  background-repeat: no-repeat;
  background-position: bottom right;
  background-size: contain;
  border-radius: ${Spacings.SPACING_2B};
  padding: ${({ $paddingTop }) => `${$paddingTop} ${Spacings.SPACING_8B}`};
  height: 100%;
  position: relative;
`;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${Spacings.SPACING_2B};
`;

const Header = styled(Typography)`
  font-size: ${Fonts.H4};
  line-height: ${Spacings.SPACING_6B};
  letter-spacing: 0%;
  font-weight: ${Fonts.SEMIBOLD};
`;

const Description = styled(Typography)`
  max-width: 207px;
  font-size: ${Fonts.P2};
  line-height: ${Spacings.SPACING_4B};
  letter-spacing: 0%;
  font-weight: ${Fonts.LIGHT};
  margin-bottom: ${Spacings.SPACING_3B};
`;

const StyledButton = styled(Button)<StyledButtonProps>`
  position: absolute;
`;

const BUTTON_VARIANTS = [
  'primary',
  'secondary',
  'brand',
  'danger',
  'danger-outline',
  'ghost',
  'ghost-green'
] as const;

const PaidTrialCard = ({ metaItem }: PaidTrialCardProps) => {
  const { plannerItemType, itemInfo } = metaItem;
  const router = useRouter();
  const { goalUID, goalSlug } = router.query as {
    goalUID: string;
    goalSlug: string;
  };
  const height: number = plannerItemTypeHeightMap[plannerItemType];

  const { item_info, description, title, header_text } = itemInfo || {};

  const { image_url, cta_text, card_css_property } = item_info || {};

  const goalSegmentData = useGoalEventProps({});

  const { background_color, text_color, description_color, button_variant } =
    card_css_property || {};

  const buttonType =
    BUTTON_VARIANTS.find(
      (item): item is ButtonVariantType => item === button_variant
    ) ?? 'brand';

  const handleClick = () => {
    segment.track(CHECKOUT_STARTED, goalSegmentData);
    router.push(`/goal/${goalSlug}/${goalUID}/subscriptions`);
  };

  return (
    <>
      <OuterWrapper $height={height}>
        <Wrapper
          $backgroundColor={background_color}
          $imageURL={image_url}
          $paddingTop={header_text ? Spacings.SPACING_7B : Spacings.SPACING_8B}
        >
          <HeaderWrapper>
            <Header variant="h4" color={text_color}>
              {title}
            </Header>
          </HeaderWrapper>
          <Description variant="p2" color={description_color || text_color}>
            {description}
          </Description>
          <StyledButton
            variant={buttonType}
            label={cta_text}
            size="small"
            type="submit"
            onClick={handleClick}
            $bottom={header_text ? Spacings.SPACING_6B : Spacings.SPACING_7B}
          />
        </Wrapper>
      </OuterWrapper>
    </>
  );
};

export default PaidTrialCard;
